import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AuthProvider } from '@/app/providers/AuthProvider';
import { BoardProvider } from '@/app/providers/BoardProvider';
import { ShellProvider } from '@/app/providers/ShellProvider';
import { ThemeProvider } from '@/app/providers/ThemeProvider';
import { ToastProvider } from '@/app/providers/ToastProvider';
import { AppLayout } from '@/components/layout/AppLayout';
import { AuthLayout } from '@/components/layout/AuthLayout';
import { ErrorBoundary } from '@/components/ui/ErrorBoundary';
import { ToastStack } from '@/components/ui/ToastStack';
import DeniedPage from '@/features/auth/DeniedPage';
import ForgotPage from '@/features/auth/ForgotPage';
import LoginPage from '@/features/auth/LoginPage';
import PendingPage from '@/features/auth/PendingPage';
import RegisterPage from '@/features/auth/RegisterPage';
import RequestAccessPage from '@/features/auth/RequestAccessPage';
import ResetPasswordPage from '@/features/auth/ResetPasswordPage';
import AcceptInvitationPage from '@/features/auth/AcceptInvitationPage';
import VerifyAccessEmailPage from '@/features/auth/VerifyAccessEmailPage';
import ClassroomEntryPage from '@/features/classroom/ClassroomEntryPage';
import ClassroomSessionPage from '@/features/classroom/ClassroomSessionPage';
import ClassroomTodayPage from '@/features/classroom/ClassroomTodayPage';
import ConsultantPage from '@/features/consultant/ConsultantPage';
import AdvisorPage from '@/features/advisor/AdvisorPage';
import CabinetRedirect from '@/features/dashboard/CabinetRedirect';
import JournalsPage from '@/features/dashboard/JournalsPage';
import NewRequestPage from '@/features/dashboard/NewRequestPage';
import PeoplePage from '@/features/dashboard/PeoplePage';
import RequestsPage from '@/features/dashboard/RequestsPage';
import HomeGate from '@/features/home/HomeGate';
import ProfilePage from '@/features/profile/ProfilePage';
import ProfileExportPage from '@/features/profile/ProfileExportPage';
import PublicProfilePage from '@/features/profile/PublicProfilePage';
import NotificationsPage from '@/features/profile/NotificationsPage';
import OwnerPage from '@/features/owner/OwnerPage';
import SupervisorPage from '@/features/supervisor/SupervisorPage';
import ResearcherPage from '@/features/researcher/ResearcherPage';
import { OperationsPartnerPage } from '@/features/operations/OperationsPartnerPage';
import { ScientificPartnerPage } from '@/features/partner/ScientificPartnerPage';
import { DepartmentHeadPage } from '@/features/department/DepartmentHeadPage';
import { DoctoralPage } from '@/features/doctoral/DoctoralPage';
import { SupervisedStudentPage } from '@/features/student/SupervisedStudentPage';
import { StudentPage } from '@/features/student/StudentPage';
import { AuthorPage } from '@/features/author/AuthorPage';
import ClientPage from '@/features/client/ClientPage';
import EditorPage from '@/features/editor/EditorPage';
import ProjectMemberPage from '@/features/project-member/ProjectMemberPage';
import { TeacherPage } from '@/features/teacher/TeacherPage';
import PortalPage from '@/features/portal/PortalPage';
import { GuestSurveyView, ResearchToolsPage } from '@/features/research-tools';
import AuditPage from '@/features/audit/AuditPage';
import SecurityPage from '@/features/security/SecurityPage';
import { I18nProvider } from '@/i18n/I18nProvider';
import { routes } from '@/lib/routes';

export const App = () => (
  <ErrorBoundary>
    <ThemeProvider>
      <I18nProvider>
        <AuthProvider>
          <ShellProvider>
            <BoardProvider>
              <ToastProvider>
                <BrowserRouter>
                  <Routes>
                    {/* Public and authenticated workspace pages in the main application shell */}
                    <Route element={<AppLayout />}>
                      <Route path={routes.home} element={<HomeGate />} />
                      <Route path={routes.app} element={<CabinetRedirect />} />
                      <Route path={`${routes.owner}/*`} element={<OwnerPage />} />
                      <Route path={`${routes.advisor}/*`} element={<AdvisorPage />} />
                      <Route path={`${routes.doctoral}/*`} element={<DoctoralPage />} />
                      <Route path="/app/learner/*" element={<DoctoralPage />} />
                      <Route path={`${routes.supervisedStudent}/*`} element={<SupervisedStudentPage />} />
                      <Route path="/app/student/*" element={<SupervisedStudentPage />} />
                      <Route path={`${routes.courseStudent}/*`} element={<StudentPage />} />
                      <Route path="/app/course-student/*" element={<StudentPage />} />
                      <Route path={`${routes.author}/*`} element={<AuthorPage />} />
                      <Route path={`${routes.client}/*`} element={<ClientPage />} />
                      <Route path={`${routes.editor}/*`} element={<EditorPage />} />
                      <Route path={`${routes.projectMember}/*`} element={<ProjectMemberPage />} />
                      <Route path="/app/member/*" element={<ProjectMemberPage />} />
                      <Route path={`${routes.supervisor}/*`} element={<SupervisorPage />} />
                      <Route path={`${routes.teacher}/*`} element={<TeacherPage />} />
                      <Route path={`${routes.researcher}/*`} element={<ResearcherPage />} />
                      <Route
                        path={`${routes.operationsPartner}/*`}
                        element={<OperationsPartnerPage />}
                      />
                      <Route path={`${routes.partner}/*`} element={<ScientificPartnerPage />} />
                      <Route path="/app/scientific-partner/*" element={<ScientificPartnerPage />} />
                      <Route path={`${routes.departmentHead}/*`} element={<DepartmentHeadPage />} />
                      <Route path={`${routes.departmentLab}/*`} element={<DepartmentHeadPage />} />
                      <Route path={routes.requests} element={<RequestsPage />} />
                      <Route path={routes.newRequest} element={<NewRequestPage />} />
                      {/* Legacy nested create layers → single Create Request Hub entry. */}
                      <Route
                        path="/app/requests/new/:legacySegment/*"
                        element={<Navigate to={routes.newRequest} replace />}
                      />
                      <Route path={routes.journals} element={<JournalsPage />} />
                      <Route path={routes.people} element={<PeoplePage />} />
                      <Route path={routes.assistant} element={<ConsultantPage />} />
                      <Route path="/app/consultant" element={<Navigate to={routes.assistant} replace />} />
                      <Route path={routes.audit} element={<AuditPage />} />
                      <Route path={routes.security} element={<SecurityPage />} />
                      <Route path={routes.notifications} element={<NotificationsPage />} />
                      <Route
                        path={routes.profile}
                        element={<Navigate to={routes.profileSection('account')} replace />}
                      />
                      <Route path="/app/profile/export/:kind" element={<ProfileExportPage />} />
                      <Route path="/app/profile/:section" element={<ProfilePage />} />
                      <Route path="/p/:academiaId" element={<PublicProfilePage />} />
                      <Route path="/app/research-tools/*" element={<ResearchToolsPage />} />

                      {/* Public portal sections share Topbar + MegaNav dropdowns */}
                      <Route
                        path={routes.portal}
                        element={<Navigate to={routes.portalSection('about')} replace />}
                      />
                      <Route path="/portal/:section" element={<PortalPage />} />
                    </Route>

                    <Route path="/guest/survey/:token" element={<GuestSurveyView />} />

                    <Route path="/auth" element={<AuthLayout />}>
                      <Route index element={<Navigate to={routes.login} replace />} />
                      <Route path="login" element={<LoginPage />} />
                      <Route path="register" element={<RegisterPage />} />
                      <Route path="request-access" element={<RequestAccessPage />} />
                      <Route path="classroom" element={<ClassroomEntryPage />} />
                      <Route path="forgot" element={<ForgotPage />} />
                      <Route path="reset-password" element={<ResetPasswordPage />} />
                      <Route path="request-access/verify" element={<VerifyAccessEmailPage />} />
                      <Route path="invitation/accept" element={<AcceptInvitationPage />} />
                      <Route path="pending" element={<PendingPage />} />
                      <Route path="denied" element={<DeniedPage />} />
                    </Route>

                    <Route path={routes.classroomToday} element={<ClassroomTodayPage />} />
                    <Route path={routes.classroomSession} element={<ClassroomSessionPage />} />

                    <Route path="*" element={<Navigate to={routes.home} replace />} />
                  </Routes>
                  <ToastStack />
                </BrowserRouter>
              </ToastProvider>
            </BoardProvider>
          </ShellProvider>
        </AuthProvider>
      </I18nProvider>
    </ThemeProvider>
  </ErrorBoundary>
);

export default App;
